import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Spin, Button, Space, Tooltip, Divider, Alert } from 'antd';
import {
  SaveOutlined,
  UndoOutlined,
  RedoOutlined,
  EyeOutlined,
  CloudUploadOutlined,
  RobotOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import LibraryPage from '../../../../components/layout/LibraryPage';
import ComponentLibrary from './ComponentLibrary';
import BuilderCanvas from './BuilderCanvas';
import PropertiesPanel from './PropertiesPanel';
import AIAssistant from './AIAssistant';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';
import TableConfigPanel from './TableConfigPanel';
import RepeaterConfigPanel from './RepeaterConfigPanel';
import { useGetTemplateQuery, useUpdateTemplateMutation, usePublishVersionMutation } from '../../../../store/api/templateApi';
import {
  setTemplate,
  updateTemplateData,
  undo,
  redo,
  selectCanUndo,
  selectCanRedo,
  selectIsDirty,
  selectTemplateData,
  clearHistory,
} from '../../../../store/slices/rfpTemplateSlice';
import {
  setDraggedItem,
  selectSelectedElement,
  toggleAIAssistant,
  toggleConditionalBuilder,
  toggleTableConfig,
  toggleRepeaterConfig,
  selectModals,
  selectDragState,
} from '../../../../store/slices/rfpBuilderSlice';
import {
  setCurrentUser,
  selectActiveUsers,
  selectIsConnected,
} from '../../../../store/slices/rfpCollaborationSlice';
import socketService from '../../../../services/socketService';
import { toast } from '../../../../components/common/Toast';
import { useUser } from '../../../../contexts/UserContext';
import { produce } from 'immer';

const { Sider, Content } = Layout;

export default function RFPBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useUser();

  const [isSaving, setIsSaving] = useState(false);

  // RTK Query
  const { data: template, isLoading, error } = useGetTemplateQuery(id);
  const [updateTemplate] = useUpdateTemplateMutation();
  const [publishVersion] = usePublishVersionMutation();

  // Redux selectors
  const canUndo = useSelector(selectCanUndo);
  const canRedo = useSelector(selectCanRedo);
  const isDirty = useSelector(selectIsDirty);
  const templateData = useSelector(selectTemplateData);
  const selectedElement = useSelector(selectSelectedElement);
  const activeUsers = useSelector(selectActiveUsers);
  const isConnected = useSelector(selectIsConnected);
  const modals = useSelector(selectModals);
  const { draggedItem } = useSelector(selectDragState);

  // Memoize initialRules calculation for ConditionalLogicBuilder
  const initialRules = useMemo(() => {
    if (selectedElement && selectedElement.type === 'question') {
      const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
      if (match) {
        const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);
        return templateData?.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx]?.questions?.[questionIdx]?.conditionalLogic || null;
      }
    } else if (selectedElement && selectedElement.type === 'container') {
      const match = selectedElement.path.match(/sheet-(\d+)(?:-section-(\d+))?(?:-category-(\d+))?/);
      if (match) {
        const [, sheetIdx, sectionIdx, categoryIdx] = match.map(Number);

        let logic = null;
        if (categoryIdx !== null && !isNaN(categoryIdx)) {
          logic = templateData?.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx]?.conditionalLogic;
        } else if (sectionIdx !== null && !isNaN(sectionIdx)) {
          logic = templateData?.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.conditionalLogic;
        } else {
          logic = templateData?.sheets?.[sheetIdx]?.conditionalLogic;
        }
        return logic || null;
      }
    }
    return null;
  }, [selectedElement, templateData]);

  // Load template into Redux on mount
  useEffect(() => {
    if (template) {
      dispatch(setTemplate(template));
    }
  }, [template, dispatch]);

  // Setup Socket.IO collaboration (optional)
  useEffect(() => {
    if (id && user) {
      const currentUser = {
        id: user.id,
        name: user.name || user.email,
      };
      dispatch(setCurrentUser(currentUser));

      // Try to connect socket (don't block if it fails)
      try {
        socketService.connect(currentUser);
        socketService.joinTemplate(id, currentUser);
      } catch (error) {
        console.warn('Socket.IO connection failed - collaboration features disabled:', error);
      }

      return () => {
        try {
          socketService.leaveTemplate(id);
        } catch (error) {
          console.warn('Socket cleanup error:', error);
        }
      };
    }
  }, [id, user, dispatch]);

  // Auto-save on Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [templateData]);

  const handleSave = async () => {
    if (!templateData || isSaving) return;

    setIsSaving(true);
    try {
      await updateTemplate({
        id,
        template_data: templateData,
      }).unwrap();

      dispatch(clearHistory());
      toast.success('Template saved');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      // Auto-save if there are unsaved changes
      if (isDirty) {
        toast.info('Saving changes before publish...');
        await handleSave();
      }

      await publishVersion({ id, version_notes: 'Published from builder' }).unwrap();
      toast.success('Template published successfully');
      navigate('/library/templates');
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish template');
    }
  };

  const handlePreview = () => {
    navigate(`/library/preview/${id}`);
  };

  const handleUndo = () => {
    if (canUndo) {
      dispatch(undo());
    }
  };

  const handleRedo = () => {
    if (canRedo) {
      dispatch(redo());
    }
  };

  const handleDragStart = (event) => {
    dispatch(setDraggedItem(event.active.data.current));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.data.current) {
      const { fieldType, label } = active.data.current;

      // Determine drop target indices from over.data
      let targetSheetIdx = 0;
      let targetSectionIdx = 0;
      let targetCategoryIdx = 0;

      if (over.data?.current) {
        const { sheetIdx, sectionIdx, categoryIdx } = over.data.current;

        // Use the provided indices directly
        if (sheetIdx !== undefined && sheetIdx !== null) {
          targetSheetIdx = sheetIdx;
        }
        if (sectionIdx !== undefined && sectionIdx !== null) {
          targetSectionIdx = sectionIdx;
        }
        if (categoryIdx !== undefined && categoryIdx !== null) {
          targetCategoryIdx = categoryIdx;
        }
      }

      console.log('Drop target:', { targetSheetIdx, targetSectionIdx, targetCategoryIdx });

      // Add field to template data at the target location
      const newData = produce(templateData, (draft) => {
        if (!draft.sheets || draft.sheets.length === 0) {
          draft.sheets = [{
            id: `sheet_${Date.now()}`,
            name: 'Main',
            sections: [],
          }];
          targetSheetIdx = 0;
        }

        const sheet = draft.sheets[targetSheetIdx];
        if (!sheet) {
          console.error(`Sheet at index ${targetSheetIdx} not found`);
          return;
        }

        if (!sheet.sections || sheet.sections.length === 0) {
          sheet.sections = [{
            id: `section_${Date.now()}`,
            name: 'General',
            categories: [],
          }];
          targetSectionIdx = 0;
        }

        const section = sheet.sections[targetSectionIdx];
        if (!section) {
          console.error(`Section at index ${targetSectionIdx} not found in sheet ${targetSheetIdx}`);
          return;
        }

        if (!section.categories || section.categories.length === 0) {
          section.categories = [{
            id: `category_${Date.now()}`,
            name: 'Questions',
            questions: [],
          }];
          targetCategoryIdx = 0;
        }

        const category = section.categories[targetCategoryIdx];
        if (!category) {
          console.error(`Category at index ${targetCategoryIdx} not found in section ${targetSectionIdx}`);
          console.log('Available categories:', section.categories.length);
          return;
        }

        if (!category.questions) {
          category.questions = [];
        }

        category.questions.push({
          id: `question_${Date.now()}`,
          question: `New ${label}`,
          field_type: fieldType,
          required: false,
          help_text: '',
          options: fieldType === 'select' || fieldType === 'multiselect' || fieldType === 'radio' || fieldType === 'checkbox' ? [] : undefined,
        });
      });

      dispatch(updateTemplateData(newData));
      toast.success(`Added ${label} field`);
    }

    dispatch(setDraggedItem(null));
  };

  if (isLoading) {
    return (
      <LibraryPage title="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" />
        </div>
      </LibraryPage>
    );
  }

  if (error) {
    return (
      <LibraryPage title="Error">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Failed to load template. Please try again.</p>
          <Button onClick={() => navigate('/library/templates')}>Back to Templates</Button>
        </div>
      </LibraryPage>
    );
  }

  const topBarActions = (
    <Space size="middle" split={<Divider type="vertical" />}>
      {/* Undo/Redo */}
      <Space size="small">
        <Tooltip title="Undo (Ctrl+Z)">
          <Button
            icon={<UndoOutlined />}
            onClick={handleUndo}
            disabled={!canUndo}
          />
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Shift+Z)">
          <Button
            icon={<RedoOutlined />}
            onClick={handleRedo}
            disabled={!canRedo}
          />
        </Tooltip>
      </Space>

      {/* Collaboration Indicator */}
      {isConnected && activeUsers.length > 0 && (
        <Tooltip title={`${activeUsers.length} user(s) active`}>
          <Button
            icon={<TeamOutlined />}
            type={activeUsers.length > 1 ? 'default' : 'text'}
          >
            {activeUsers.length}
          </Button>
        </Tooltip>
      )}

      {/* AI Assistant */}
      <Tooltip title="AI Assistant">
        <Button
          icon={<RobotOutlined />}
          onClick={() => dispatch(toggleAIAssistant())}
        />
      </Tooltip>

      {/* Preview */}
      <Tooltip title="Preview">
        <Button icon={<EyeOutlined />} onClick={handlePreview} />
      </Tooltip>

      {/* Save */}
      <Button
        type={isDirty ? 'primary' : 'default'}
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={isSaving}
      >
        {isDirty ? 'Save' : 'Saved'}
      </Button>

      {/* Publish */}
      <Button
        type="primary"
        icon={<CloudUploadOutlined />}
        onClick={handlePublish}
        style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
      >
        Publish
      </Button>
    </Space>
  );

  return (
    <LibraryPage
      title={template?.name || 'Template Builder'}
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Templates', path: '/library/templates' },
        { label: template?.name || 'Builder' },
      ]}
      actions={topBarActions}
      fullHeight
    >
      {isDirty && (
        <Alert
          message="You have unsaved changes"
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          banner
        />
      )}
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Layout style={{ height: isDirty ? 'calc(100vh - 240px)' : 'calc(100vh - 180px)', background: '#fff' }}>
          {/* Left Panel - Component Library */}
          <Sider
            width={280}
            theme="light"
            style={{
              borderRight: '1px solid #E5E7EB',
              overflow: 'auto',
            }}
          >
            <ComponentLibrary />
          </Sider>

          {/* Center Panel - Builder Canvas */}
          <Content
            style={{
              padding: '24px',
              overflow: 'auto',
              background: '#F9FAFB',
            }}
          >
            <BuilderCanvas />
          </Content>

          {/* Right Panel - Properties */}
          <Sider
            width={320}
            theme="light"
            style={{
              borderLeft: '1px solid #E5E7EB',
              overflow: 'auto',
            }}
          >
            <PropertiesPanel selectedElement={selectedElement} />
          </Sider>
        </Layout>

        <DragOverlay dropAnimation={null}>
          {draggedItem && (
            <div
              style={{
                padding: '12px 16px',
                background: '#8B5CF6',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                cursor: 'grabbing',
                zIndex: 10000,
              }}
            >
              {draggedItem.label}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      <AIAssistant
        open={modals.aiAssistant}
        onClose={() => dispatch(toggleAIAssistant())}
        templateId={id}
        onQuestionsGenerated={(questions) => {
          // Handle generated questions
          toast.success('Questions added to template');
        }}
      />

      <ConditionalLogicBuilder
        open={modals.conditionalBuilder}
        onClose={() => dispatch(toggleConditionalBuilder())}
        onSave={(rules) => {
          if (selectedElement && selectedElement.type === 'question') {
            // Parse the path to get indices
            const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);

              const newData = produce(templateData, (draft) => {
                const question = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
                question.conditionalLogic = rules;
              });

              dispatch(updateTemplateData(newData));
              toast.success('Conditional logic saved');
            }
          } else if (selectedElement && selectedElement.type === 'container') {
            // For containers (sections, categories), save conditional logic too
            const match = selectedElement.path.match(/sheet-(\d+)(?:-section-(\d+))?(?:-category-(\d+))?/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx] = match.map(Number);

              const newData = produce(templateData, (draft) => {
                let target;
                if (categoryIdx !== null && !isNaN(categoryIdx)) {
                  target = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx];
                } else if (sectionIdx !== null && !isNaN(sectionIdx)) {
                  target = draft.sheets[sheetIdx].sections[sectionIdx];
                } else {
                  target = draft.sheets[sheetIdx];
                }
                target.conditionalLogic = rules;
              });

              dispatch(updateTemplateData(newData));
              toast.success('Conditional logic saved');
            }
          }
        }}
        initialRules={initialRules}
        currentFieldPath={selectedElement?.path || null}
      />

      <TableConfigPanel
        open={modals.tableConfig}
        onClose={() => dispatch(toggleTableConfig())}
        onSave={(config) => {
          if (selectedElement && selectedElement.type === 'question') {
            // Parse the path to get indices
            const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);

              const newData = produce(templateData, (draft) => {
                const question = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
                question.tableConfig = config;
              });

              dispatch(updateTemplateData(newData));
              toast.success('Table configuration saved');
            }
          }
        }}
        initialConfig={(() => {
          if (selectedElement && selectedElement.type === 'question') {
            const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);
              return templateData?.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx]?.questions?.[questionIdx]?.tableConfig || {};
            }
          }
          return {};
        })()}
      />

      <RepeaterConfigPanel
        open={modals.repeaterConfig}
        onClose={() => dispatch(toggleRepeaterConfig())}
        onSave={(config) => {
          if (selectedElement && selectedElement.type === 'question') {
            // Parse the path to get indices
            const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);

              const newData = produce(templateData, (draft) => {
                const question = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
                question.repeaterConfig = config;
              });

              dispatch(updateTemplateData(newData));
              toast.success('Repeater configuration saved');
            }
          }
        }}
        initialConfig={(() => {
          if (selectedElement && selectedElement.type === 'question') {
            const match = selectedElement.path.match(/sheet-(\d+)-section-(\d+)-category-(\d+)-question-(\d+)/);
            if (match) {
              const [, sheetIdx, sectionIdx, categoryIdx, questionIdx] = match.map(Number);
              return templateData?.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx]?.questions?.[questionIdx]?.repeaterConfig || {};
            }
          }
          return {};
        })()}
      />
    </LibraryPage>
  );
}
